import { useState, useEffect, FormEvent } from 'react'
import FormField from '../../../components/common/FormField'
import FileUpload from '../../../components/common/FileUpload'
import TiptapEditor from './TiptapEditor'
import Button from '../../../components/common/Button'
import { getImageUrl } from '../../../utils/helpers'
import api from '../../../services/api'

export interface CoverMedia {
  id: number
  file_name: string
  file_path: string
  url: string
  mime_type?: string
  alt_text?: string
  title?: string
}

export interface Article {
  id?: number
  title: string
  slug: string
  excerpt: string
  content: string
  cover_media_id: number | null
  cover_media?: CoverMedia | null
  status: 'draft' | 'published' | 'archived'
  published_at: string
  created_at?: string
  updated_at?: string
}

interface ArticleFormProps {
  article?: Article
  onSubmit: (article: Partial<Article>) => void
  onCancel: () => void
  isLoading?: boolean
}

function ArticleForm({ article, onSubmit, onCancel, isLoading }: ArticleFormProps) {
  const [formData, setFormData] = useState<Partial<Article>>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_media_id: null,
    cover_media: null,
    status: 'draft',
    published_at: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [autoSlug, setAutoSlug] = useState(true)
  const [sourceUrl, setSourceUrl] = useState('')
  const [rewriting, setRewriting] = useState(false)
  const [rewriteError, setRewriteError] = useState('')

  useEffect(() => {
    if (article) {
      setFormData(article)
      setAutoSlug(false)
    }
  }, [article])

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/-+/g, '-') // Replace multiple - with single -
      .replace(/^-|-$/g, '') // Remove leading/trailing -
  }

  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: autoSlug ? generateSlug(title) : prev.slug,
    }))
  }

  const handleSlugChange = (slug: string) => {
    setAutoSlug(false)
    setFormData((prev) => ({ ...prev, slug }))
  }

  const handleFileUpload = (fileData: CoverMedia) => {
    setFormData((prev) => ({
      ...prev,
      cover_media_id: fileData.id,
      cover_media: fileData,
    }))
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title?.trim()) {
      newErrors.title = 'Le titre est requis'
    }

    if (!formData.slug?.trim()) {
      newErrors.slug = 'Le slug est requis'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, chiffres et tirets'
    }

    if (!formData.excerpt?.trim()) {
      newErrors.excerpt = 'L\'extrait est requis'
    }

    const contentEmpty = !formData.content?.trim() || formData.content === '<p></p>'
    if (contentEmpty) {
      newErrors.content = 'Le contenu est requis'
    }

    if (formData.status === 'published' && !formData.published_at) {
      newErrors.published_at = 'La date de publication est requise pour un article publié'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleRewrite = async () => {
    setRewriteError('')
    setRewriting(true)
    try {
      const result = await api.post<{ titre: string; html: string; extrait?: string }>('/articles/rewrite', {
        lien_article: sourceUrl,
      })
      const newTitle = result.titre
      setFormData((prev) => ({
        ...prev,
        title: newTitle,
        content: result.html,
        slug: generateSlug(newTitle),
        ...(result.extrait ? { excerpt: result.extrait } : {}),
      }))
      setAutoSlug(true)
    } catch (err) {
      setRewriteError(err instanceof Error ? err.message : 'Erreur lors de la réécriture')
    } finally {
      setRewriting(false)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="article-form">
      <div className="form-grid">
        <div className="form-main">
          {!article && (
            <div className="form-field">
              <label htmlFor="source-url">Article source</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="source-url"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://example.com/article-source"
                  disabled={rewriting}
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  onClick={handleRewrite}
                  disabled={!sourceUrl.trim() || rewriting}
                >
                  {rewriting ? 'Réécriture en cours...' : 'Réécrire avec IA'}
                </Button>
              </div>
              {rewriteError && <span className="field-error">{rewriteError}</span>}
            </div>
          )}

          <FormField
            label="Titre"
            id="title"
            value={formData.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            error={errors.title}
            required
            placeholder="Titre de l'article"
          />

          <div className="form-field">
            <label htmlFor="slug">
              Slug (URL)
              <span className="required">*</span>
              {autoSlug && <span className="hint"> (généré automatiquement)</span>}
            </label>
            <input
              id="slug"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              className={errors.slug ? 'error' : ''}
              placeholder="mon-article-slug"
            />
            {errors.slug && <span className="field-error">{errors.slug}</span>}
          </div>

          <FormField
            label="Extrait"
            id="excerpt"
            textarea
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            error={errors.excerpt}
            required
            rows={3}
            placeholder="Court résumé de l'article (affiché dans les listes)"
          />

          <TiptapEditor
            label="Contenu"
            value={formData.content || ''}
            onChange={(content) => setFormData({ ...formData, content })}
            required
          />
          {errors.content && <span className="field-error">{errors.content}</span>}
        </div>

        <div className="form-sidebar">
          <div className="form-section">
            <h3>Publication</h3>

            <div className="form-field">
              <label htmlFor="status">
                Statut
                <span className="required">*</span>
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as Article['status'] })
                }
              >
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="archived">Archivé</option>
              </select>
            </div>

            <FormField
              label="Date de publication"
              id="published_at"
              type="datetime-local"
              value={formData.published_at}
              onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
              error={errors.published_at}
              required={formData.status === 'published'}
            />
          </div>

          <div className="form-section">
            <h3>Image de couverture</h3>

            {formData.cover_media && (
              <div className="cover-preview">
                <img src={getImageUrl(formData.cover_media.file_path)} alt="Cover preview" />
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => setFormData({ ...formData, cover_media_id: null, cover_media: null })}
                >
                  Supprimer
                </button>
              </div>
            )}

            <FileUpload
              onUpload={handleFileUpload}
              accept="image/*"
              label={formData.cover_media ? 'Changer l\'image' : 'Choisir une image'}
            />
          </div>

          <div className="form-actions">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Enregistrement...' : article ? 'Mettre à jour' : 'Créer'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default ArticleForm
